import React, { useEffect } from 'react';
import { Card, Badge, Spinner } from 'flowbite-react';
import { observer } from 'mobx-react-lite';
import {
  HiUsers,
  HiClipboardCheck,
  HiBell,
  HiTrendingUp,
  HiTrendingDown
} from 'react-icons/hi';
import { registrationStore } from '../stores/registrationStore';
import { screenStore } from '../stores/screenStore';
import { notificationStore } from '../stores/notificationStore';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<any>;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  color?: string;
  isLoading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon: Icon,
  trend,
  trendValue,
  color = 'blue',
  isLoading = false
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
  };

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          {isLoading ? (
            <div className="flex items-center h-8">
              <Spinner size="md" />
            </div>
          ) : (
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          )}
          {!isLoading && trend && trendValue && (
            <div className="flex items-center mt-2">
              {trend === 'up' ? (
                <HiTrendingUp className="w-4 h-4 text-green-500 mr-1" />
              ) : trend === 'down' ? (
                <HiTrendingDown className="w-4 h-4 text-red-500 mr-1" />
              ) : null}
              <span className={`text-sm ${
                trend === 'up' ? 'text-green-600' :
                trend === 'down' ? 'text-red-600' :
                'text-gray-600'
              }`}>
                {trendValue}
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color as keyof typeof colorClasses]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  );
};

const DashboardPage: React.FC = observer(() => {
  useEffect(() => {
    // Fetch statistics independently (not with Promise.all)
    registrationStore.fetchTodayRegistrationsCount();
    registrationStore.fetchTodayWaitingGuestsCount();
    screenStore.fetchLatestScreenTime();
    screenStore.fetchLLMTokenUsage();
    notificationStore.fetchRecentNotifications(20);
  }, []);

  const stats = {
    todayRegistrations: registrationStore.todayRegistrationsCount,
    waitingGuests: registrationStore.waitingRegistrationsCount,
    totalScreens: screenStore.totalScreensCount,
    latestScreenTime: screenStore.latestScreenTime
      ? new Date(screenStore.latestScreenTime).toLocaleTimeString('ja-JP')
      : '未取得',
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">ダッシュボード</h1>
        <p className="text-gray-600">システムの稼働状況</p>
      </div>

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="本日の登録数"
          value={stats.todayRegistrations}
          icon={HiClipboardCheck}
          color="blue"
          isLoading={registrationStore.loadingStates.todayRegistrations}
        />
        <StatCard
          title="本日の待機中ゲスト"
          value={stats.waitingGuests}
          icon={HiUsers}
          color="green"
          isLoading={registrationStore.loadingStates.todayWaitingGuests}
        />
        <StatCard
          title="直近のアップロード時刻"
          value={stats.latestScreenTime}
          icon={HiBell}
          color="red"
          isLoading={screenStore.loadingStates.latestScreenTime}
        />
      </div>

      {/* LLM使用量 */}
      {(screenStore.llmTokenUsage || screenStore.llmTokenUsageLoading) && (
        <Card>
          <h3 className="text-lg font-semibold mb-4">LLM使用量</h3>
          {screenStore.llmTokenUsageLoading ? (
            <div className="flex items-center justify-center h-16">
              <Spinner size="md" />
            </div>
          ) : screenStore.llmTokenUsage ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                  <tr>
                    <th className="px-4 py-2"></th>
                    <th className="px-4 py-2 text-right">入力トークン</th>
                    <th className="px-4 py-2 text-right">出力トークン</th>
                    <th className="px-4 py-2 text-right">解析枚数</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100">
                    <td className="px-4 py-2 font-medium text-gray-700">本日</td>
                    <td className="px-4 py-2 text-right text-gray-900">
                      {screenStore.llmTokenUsage.today.inputTokens.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-900">
                      {screenStore.llmTokenUsage.today.outputTokens.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-900">
                      {screenStore.llmTokenUsage.today.screenCount.toLocaleString()}
                    </td>
                  </tr>
                  <tr className="border-t border-gray-100">
                    <td className="px-4 py-2 font-medium text-gray-700">今月</td>
                    <td className="px-4 py-2 text-right text-gray-900">
                      {screenStore.llmTokenUsage.thisMonth.inputTokens.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-900">
                      {screenStore.llmTokenUsage.thisMonth.outputTokens.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-900">
                      {screenStore.llmTokenUsage.thisMonth.screenCount.toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : null}
        </Card>
      )}

      {/* 最近の通知履歴 */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">最近の通知履歴</h3>
        <div className="space-y-3">
          {notificationStore.notifications.length === 0 ? (
            <p className="text-gray-500 text-sm">通知履歴がありません</p>
          ) : (
            notificationStore.recentNotifications.map((notification) => {
              const time = new Date(notification.notifiedAt).toLocaleTimeString('ja-JP', {
                hour: '2-digit',
                minute: '2-digit',
              });

              // ゲスト管理と同じ色設定にマッピング
              const getNotificationBadge = () => {
                const typeConfig: Record<string, { color: string; label: string }> = {
                  'calling': { color: 'blue', label: '呼び出し中' },
                  'waiting': { color: 'yellow', label: '呼び出し待機中' },
                  'called': { color: 'green', label: '呼び出し済み' },
                };

                const config = typeConfig[notification.notificationType] || { color: 'gray', label: notification.notificationType };
                return (
                  <Badge color={config.color as any}>{config.label}</Badge>
                );
              };

              return (
                <div key={notification.id} className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500">{time}</span>
                  <span className="text-gray-700">番号 {notification.callNumber}</span>
                  <Badge color="info" size="sm">LINE UID: {notification.lineUserId}</Badge>
                  {getNotificationBadge()}
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* グラフエリア（プレースホルダー） */}
      {/* <Card>
        <h3 className="text-lg font-semibold mb-4">時間帯別登録数</h3>
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
          <p className="text-gray-500">グラフ表示エリア</p>
        </div>
      </Card> */}
    </div>
  );
});

export default DashboardPage;